import { Guid } from "@/store/views/actions";
import { MessageBroadcastChannel } from "@/utils/messageBroadcastChannel";
import { EventHandlerMap } from "@/types/BroadcastChannelTypes";

export default class ViewsEventBus {

	private eventHandlers: EventHandlerMap;
	private eventBusChannel: MessageBroadcastChannel;
	public eventBusGUID: string;

	constructor(overrideGUID: string = "") {
		this.eventHandlers = {};
		this.setupBroadCastChannel(overrideGUID);
	}

	/**
	 * Sets up a broadcast channel.
	 * @param overrideGUID The GUID this broadcast channel should use, a new GUID will be generated if this is left blank.
	 */
	public setupBroadCastChannel(overrideGUID: string = "") {
		if (overrideGUID) {
			this.eventBusGUID = overrideGUID;
		}
		else {
			this.eventBusGUID = Guid.newGuid();
		}

		this.eventBusChannel = new MessageBroadcastChannel(`ViewsEventBus${this.eventBusGUID}`);
	}

	/**
	 * Adds a handler to the specified event (handlers are methods that are invoked upon event emission)
	 * @param eventName The name of the event we want to manipulate (creates it if it does not yet exist)
	 * @param handler The handler to be added to the event.
	 */
	public $on(eventName: string, handler: (eventData: any) => void) {
		if (!this.eventHandlers[eventName]) {
			this.eventHandlers[eventName] = [];
		}
		this.eventHandlers[eventName].push(handler);

		this.eventBusChannel.addListener(eventName, handler);
	}

	/**
	 * Disables/deletes a handler from the specified event.
	 * @param eventName The name of the event we are manipulating.
	 * @param handler The handler we are removing the specified event.
	 */
	public $off(eventName: string, handler: (eventData: any) => void) {
		if (!this.eventHandlers[eventName]) return;

		let handlerIndex = this.eventHandlers[eventName].findIndex(h => h === handler);
		this.eventHandlers[eventName].splice(handlerIndex, 1);

		this.eventBusChannel.removeSpecificListener(eventName, handler);
	}

	/**
	 * Emit the specified event and handle the provided data.
	 * @param eventName The name of the event to be triggered
	 * @param eventData Data that is provided along with this emission/invocation to be handled by all event handlers.
	 */
	public $emit(eventName: string, eventData: any) {
		this.triggerEventForAllRelevantHandlers(eventName, eventData);
	}

	/**
	 * Triggers events that need to be handled.
	 * @param eventName The name of the event to be triggered
	 * @param eventData Data that is provided along with this emission/invocation to be handled by all event handlers.
	 * @returns
	 */
	private triggerEventForAllRelevantHandlers(eventName: string, eventData: any) {
		this.eventBusChannel.postMessage(eventName, eventData);

		if (this.eventHandlers[eventName]) {

			// Fire the event to all the handlers listening
			this.eventHandlers[eventName].forEach(handler => {
				handler(eventData);
			});
		}
	}
}
